import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import apiService from "../../services/api";
import { formatCurrency } from "../../utils/currencyUtils";
import { triggerBlobDownload } from "../../utils/downloadUtils";
import WisePaymentModal from "./WisePaymentModal";

const StatusBadge = ({ status }) => {
  if (!status) return null;
  const cfg = {
    PAID:   { bg: "#dcfce7", color: "#166534", text: "Paid" },
    LOCKED: { bg: "#fef9c3", color: "#854d0e", text: "Unpaid" },
    DRAFT:  { bg: "#f1f5f9", color: "#64748b", text: "Draft" },
  }[status] || { bg: "#f1f5f9", color: "#64748b", text: status };
  return (
    <span style={{
      display: "inline-block",
      fontSize: "0.7rem",
      fontWeight: "600",
      padding: "0.15rem 0.5rem",
      borderRadius: "9999px",
      background: cfg.bg,
      color: cfg.color,
    }}>
      {cfg.text}
    </span>
  );
};

const EmailStatusBadge = ({ status }) => {
  const cfg = {
    SENT:       { bg: "#dcfce7", color: "#166534", text: "Payslip: Sent" },
    PENDING:    { bg: "#fef9c3", color: "#854d0e", text: "Payslip: Queued" },
    PROCESSING: { bg: "#fef9c3", color: "#854d0e", text: "Payslip: Sending…" },
    FAILED:     { bg: "#fee2e2", color: "#991b1b", text: "Payslip: Failed" },
  }[status] || null;

  if (!cfg) return null;
  return (
    <span style={{
      display: "inline-block",
      fontSize: "0.7rem",
      fontWeight: "600",
      padding: "0.15rem 0.5rem",
      borderRadius: "9999px",
      background: cfg.bg,
      color: cfg.color,
    }}>
      {cfg.text}
    </span>
  );
};

const MarkPaidModal = ({ tutor, yearMonth, onConfirm, onClose }) => {
  const [notes, setNotes] = useState("");
  const overlay = {
    position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.4)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
  };
  return (
    <div style={overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{ background: "white", borderRadius: "0.5rem", padding: "1.25rem", width: "min(90vw, 400px)", boxShadow: "0 10px 40px rgba(0,0,0,0.2)", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: "700", color: "#001272" }}>Mark as Paid</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: "1.1rem", cursor: "pointer", color: "#64748b" }}>×</button>
        </div>
        <p style={{ margin: 0, fontSize: "0.875rem", color: "#374151" }}>
          Mark <strong>{tutor.tutorName}</strong> as paid for <strong>{yearMonth}</strong>?
        </p>
        <div>
          <label style={{ display: "block", fontSize: "0.8rem", fontWeight: "600", color: "#374151", marginBottom: "0.3rem" }}>
            Notes <span style={{ fontWeight: "400", color: "#6b7280" }}>(optional)</span>
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. Paid via PayPal, bank transfer ref #123…"
            rows={3}
            style={{ width: "100%", borderRadius: "0.25rem", border: "1px solid #e2e8f0", padding: "0.5rem", fontSize: "0.875rem", resize: "vertical", boxSizing: "border-box", fontFamily: "inherit" }}
          />
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
          <button onClick={onClose} style={{ backgroundColor: "#f1f5f9", color: "#374151", border: "1px solid #e2e8f0", borderRadius: "0.25rem", padding: "0.4rem 0.9rem", fontSize: "0.875rem", fontWeight: "600", cursor: "pointer" }}>Cancel</button>
          <button onClick={() => onConfirm(notes)} style={{ backgroundColor: "#16a34a", color: "white", border: "none", borderRadius: "0.25rem", padding: "0.4rem 0.9rem", fontSize: "0.875rem", fontWeight: "600", cursor: "pointer" }}>Confirm</button>
        </div>
      </div>
    </div>
  );
};

const WisePayoutBadge = ({ status, onClick }) => {
  const cfg = {
    TRANSFER_CREATED: { bg: "#eff6ff", color: "#1d4ed8", text: "Wise: In Progress" },
    FUNDED:           { bg: "#eff6ff", color: "#1d4ed8", text: "Wise: Funded" },
    COMPLETED:        { bg: "#dcfce7", color: "#166534", text: "Wise: Sent" },
    FAILED:           { bg: "#fee2e2", color: "#991b1b", text: "Wise: Failed" },
  }[status];
  if (!cfg) return null;
  return (
    <span
      onClick={onClick}
      style={{
        display: "inline-block", fontSize: "0.7rem", fontWeight: "600",
        padding: "0.15rem 0.5rem", borderRadius: "9999px",
        background: cfg.bg, color: cfg.color,
        cursor: onClick ? "pointer" : "default",
        textDecoration: onClick ? "underline dotted" : "none",
      }}
    >
      {cfg.text}
    </span>
  );
};

const WiseTransferModal = ({ tutor, yearMonth, onClose }) => {
  const overlay = {
    position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.4)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
  };
  const statusCfg = {
    TRANSFER_CREATED: { bg: "#eff6ff", color: "#1d4ed8", text: "In Progress" },
    FUNDED:           { bg: "#eff6ff", color: "#1d4ed8", text: "Funded" },
    COMPLETED:        { bg: "#dcfce7", color: "#166534", text: "Sent" },
    FAILED:           { bg: "#fee2e2", color: "#991b1b", text: "Failed" },
  }[tutor.wisePayoutStatus] || { bg: "#f1f5f9", color: "#64748b", text: tutor.wisePayoutStatus };

  const copyToClipboard = (text) => navigator.clipboard?.writeText(text);

  return (
    <div style={overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{ background: "white", borderRadius: "0.5rem", padding: "1.25rem", width: "min(90vw, 420px)", boxShadow: "0 10px 40px rgba(0,0,0,0.2)", display: "flex", flexDirection: "column", gap: "1rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: "700", color: "#001272" }}>Wise Transfer Details</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: "1.1rem", cursor: "pointer", color: "#64748b" }}>×</button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
          <Row label="Tutor" value={tutor.tutorName} />
          <Row label="Period" value={yearMonth} />
          <Row label="Sent" value={`$${parseFloat(tutor.net).toFixed(2)} USD`} />
          {tutor.wiseTargetAmount && tutor.wiseTargetCurrency && (
            <Row
              label="Recipient receives"
              value={`${parseFloat(tutor.wiseTargetAmount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${tutor.wiseTargetCurrency}`}
            />
          )}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: "0.8rem", color: "#6b7280", fontWeight: "500" }}>Status</span>
            <span style={{ fontSize: "0.8rem", fontWeight: "600", padding: "0.15rem 0.5rem", borderRadius: "9999px", background: statusCfg.bg, color: statusCfg.color }}>{statusCfg.text}</span>
          </div>
          {tutor.wiseTransferId && (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "0.8rem", color: "#6b7280", fontWeight: "500" }}>Transfer ID</span>
              <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                <span style={{ fontSize: "0.875rem", fontWeight: "700", fontFamily: "monospace", color: "#111827" }}>{tutor.wiseTransferId}</span>
                <button
                  onClick={() => copyToClipboard(String(tutor.wiseTransferId))}
                  title="Copy transfer ID"
                  style={{ background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: "0.25rem", padding: "0.2rem 0.5rem", fontSize: "0.7rem", cursor: "pointer", color: "#374151" }}
                >
                  Copy
                </button>
              </div>
            </div>
          )}
        </div>

        <p style={{ margin: 0, fontSize: "0.75rem", color: "#9ca3af" }}>
          Look up this transfer ID in your Wise dashboard to track delivery status.
        </p>

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ backgroundColor: "#f1f5f9", color: "#374151", border: "1px solid #e2e8f0", borderRadius: "0.25rem", padding: "0.4rem 0.9rem", fontSize: "0.875rem", fontWeight: "600", cursor: "pointer" }}>Close</button>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
    <span style={{ fontSize: "0.8rem", color: "#6b7280", fontWeight: "500" }}>{label}</span>
    <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#111827" }}>{value}</span>
  </div>
);

const fmtAmt = (value) => value == null ? "—" : formatCurrency(value);

const PayrollTable = ({ yearMonth, payrollRun, isSuperAdmin }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isMobile, setIsMobile] = useState(false);
  const [sortBy, setSortBy] = useState("name"); // "name" | "net"
  const [loadingTutors, setLoadingTutors] = useState(new Set()); // canonicalTutorIds in-flight for payment
  const [sendingEmails, setSendingEmails] = useState(new Set()); // canonicalTutorIds in-flight for email
  const [downloadingPdfs, setDownloadingPdfs] = useState(new Set()); // canonicalTutorIds in-flight for PDF download
  const [selectedTutorIds, setSelectedTutorIds] = useState(new Set());
  const [showWiseModal, setShowWiseModal] = useState(false);
  const [markPaidTarget, setMarkPaidTarget] = useState(null);
  const [wiseDetailTutor, setWiseDetailTutor] = useState(null);
  const [tutorFilter, setTutorFilter] = useState("all"); // "all" | "unpaid"

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const hasPayroll = payrollRun != null;
  const isLocked = payrollRun?.status === "LOCKED";

  const {
    data: tutors = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["payrollTutors", yearMonth],
    queryFn: () => apiService.getPayrollTutors(yearMonth),
    enabled: hasPayroll,
    staleTime: 60 * 1000,
  });

  const { data: emailStatuses = [] } = useQuery({
    queryKey: ["emailStatus", yearMonth],
    queryFn: () => apiService.getEmailStatus(yearMonth),
    enabled: isLocked,
    refetchInterval: isLocked ? 15_000 : false,
    staleTime: 10_000,
  });

  const emailStatusMap = Object.fromEntries(
    emailStatuses.map((e) => [e.canonicalTutorId, e])
  );

  const invalidateTutors = () =>
    queryClient.invalidateQueries({ queryKey: ["payrollTutors", yearMonth] });

  const invalidateEmailStatus = () =>
    queryClient.invalidateQueries({ queryKey: ["emailStatus", yearMonth] });

  const isWiseEligible = (t) =>
    t.wiseRecipientId != null &&
    t.paymentStatus !== "PAID" &&
    (t.wisePayoutStatus == null || t.wisePayoutStatus === "FAILED");

  const eligibleForWise = tutors.filter(isWiseEligible);

  const allWiseSelected =
    eligibleForWise.length > 0 &&
    eligibleForWise.every((t) => selectedTutorIds.has(t.canonicalTutorId));

  const toggleSelectAll = () => {
    if (allWiseSelected) {
      setSelectedTutorIds(new Set());
    } else {
      setSelectedTutorIds(new Set(eligibleForWise.map((t) => t.canonicalTutorId)));
    }
  };

  const toggleSelectTutor = (tutorId) => {
    setSelectedTutorIds((prev) => {
      const next = new Set(prev);
      if (next.has(tutorId)) next.delete(tutorId);
      else next.add(tutorId);
      return next;
    });
  };

  const handleMarkPaid = (tutor, currentlyPaid) => {
    if (currentlyPaid) {
      handleUnmarkPaid(tutor.canonicalTutorId);
    } else {
      setMarkPaidTarget(tutor);
    }
  };

  const handleUnmarkPaid = async (tutorId) => {
    setLoadingTutors((prev) => new Set(prev).add(tutorId));
    try {
      await apiService.unmarkTutorPaid(yearMonth, tutorId);
      await invalidateTutors();
    } catch (err) {
      alert(`Failed: ${err.message}`);
    } finally {
      setLoadingTutors((prev) => { const next = new Set(prev); next.delete(tutorId); return next; });
    }
  };

  const handleConfirmPaid = async (notes) => {
    const tutor = markPaidTarget;
    setMarkPaidTarget(null);
    setLoadingTutors((prev) => new Set(prev).add(tutor.canonicalTutorId));
    try {
      await apiService.markTutorPaid(yearMonth, tutor.canonicalTutorId, notes);
      await invalidateTutors();
    } catch (err) {
      alert(`Failed: ${err.message}`);
    } finally {
      setLoadingTutors((prev) => { const next = new Set(prev); next.delete(tutor.canonicalTutorId); return next; });
    }
  };

  const handleSendEmail = async (tutorId) => {
    setSendingEmails((prev) => new Set(prev).add(tutorId));
    try {
      await apiService.queueTutorEmail(yearMonth, tutorId);
      await invalidateEmailStatus();
    } catch (err) {
      alert(`Failed to queue email: ${err.message}`);
    } finally {
      setSendingEmails((prev) => {
        const next = new Set(prev);
        next.delete(tutorId);
        return next;
      });
    }
  };

  const handleDownloadPdf = async (tutorId, tutorName) => {
    setDownloadingPdfs((prev) => new Set(prev).add(tutorId));
    try {
      const blob = await apiService.downloadTutorPdf(yearMonth, tutorId);
      const safeName = (tutorName || tutorId).replace(/[^a-zA-Z0-9_\- ]/g, "_");
      triggerBlobDownload(blob, `payroll_${yearMonth}_${safeName}.pdf`);
    } catch (err) {
      alert("PDF download failed: " + err.message);
    } finally {
      setDownloadingPdfs((prev) => { const next = new Set(prev); next.delete(tutorId); return next; });
    }
  };

  const pdfBtn = (tutor) => {
    if (tutor.isEmployee && !isSuperAdmin) return null;
    const busy = downloadingPdfs.has(tutor.canonicalTutorId);
    return (
      <button
        disabled={busy}
        onClick={() => handleDownloadPdf(tutor.canonicalTutorId, tutor.tutorName)}
        title="Download PDF pay statement"
        style={{
          border: "none",
          borderRadius: "0.25rem",
          padding: "0.3rem 0.6rem",
          fontSize: "0.75rem",
          fontWeight: "600",
          cursor: busy ? "not-allowed" : "pointer",
          opacity: busy ? 0.6 : 1,
          backgroundColor: "#f1f5f9",
          color: "#001272",
          whiteSpace: "nowrap",
        }}
      >
        {busy ? "…" : "↓ PDF"}
      </button>
    );
  };

  const paidCount = tutors.filter((t) => t.paymentStatus === "PAID").length;
  const allPaid = tutors.length > 0 && paidCount === tutors.length;

  const sentEmailCount = emailStatuses.filter((e) => e.status === "SENT").length;

  const styles = {
    card: {
      background: "white",
      borderRadius: "0.25rem",
      boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
      padding: "1rem",
      border: "1px solid #e2e8f0",
      marginBottom: "1rem",
      overflow: "hidden",
    },
    cardTitle: { fontSize: "1.25rem", fontWeight: "600", color: "#001272", margin: "0 0 0.75rem 0" },
    th: {
      backgroundColor: "#f8fafc",
      border: "1px solid #e2e8f0",
      padding: "0.75rem",
      fontSize: "0.75rem",
      fontWeight: "600",
      color: "#64748b",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
    },
    thRight: {
      backgroundColor: "#f8fafc",
      border: "1px solid #e2e8f0",
      padding: "0.75rem",
      fontSize: "0.75rem",
      fontWeight: "600",
      color: "#64748b",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      textAlign: "right",
    },
    td: { border: "1px solid #e2e8f0", padding: "0.75rem", fontSize: "0.875rem", color: "#4b5563", verticalAlign: "top" },
    tdRight: { border: "1px solid #e2e8f0", padding: "0.75rem", fontSize: "0.875rem", color: "#4b5563", verticalAlign: "top", textAlign: "right" },
    summaryRow: { backgroundColor: "#f8fafc", borderTop: "2px solid #e2e8f0" },
    viewBtn: {
      backgroundColor: "#001272", color: "white", border: "none",
      borderRadius: "0.25rem", padding: "0.375rem 0.75rem",
      fontSize: "0.875rem", fontWeight: "500", cursor: "pointer",
    },
    mobileCard: { backgroundColor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "0.25rem", padding: "0.75rem", marginBottom: "0.75rem" },
    mobileHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.5rem" },
    mobileGrid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "0.5rem", marginTop: "0.5rem" },
    mobileLabel: { fontSize: "0.75rem", color: "#64748b", fontWeight: "500" },
    mobileValue: { fontSize: "0.875rem", color: "#4b5563", fontWeight: "500" },
  };

  const filteredTutors = tutorFilter === "unpaid"
    ? tutors.filter((t) => t.paymentStatus !== "PAID")
    : tutors;

  const sortedTutors = [...filteredTutors].sort((a, b) => {
    if (sortBy === "net") return ((b.net ?? -Infinity) - (a.net ?? -Infinity));
    return (a.tutorName || "").localeCompare(b.tutorName || "");
  });

  // Only sum visible (non-masked) amounts for totals
  const visibleTutors = tutors.filter((t) => t.gross != null);
  const totalGross = visibleTutors.reduce((s, t) => s + (t.gross || 0), 0);
  const totalDeductions = visibleTutors.reduce((s, t) => s + (t.deductions || 0), 0);
  const totalAdjustments = visibleTutors.reduce((s, t) => s + (parseFloat(t.adjustments) || 0), 0);
  const totalNet = visibleTutors.reduce((s, t) => s + (t.net || 0), 0);
  const hasAdjustments = tutors.some((t) => t.adjustments != null && parseFloat(t.adjustments) !== 0);
  const hasMaskedTutors = tutors.some((t) => t.isEmployee && t.gross == null);

  const payBtn = (tutor) => {
    const paid = tutor.paymentStatus === "PAID";
    const hasActiveWisePayout = tutor.wisePayoutStatus != null && tutor.wisePayoutStatus !== "FAILED";
    if (hasActiveWisePayout) return null;
    const busy = loadingTutors.has(tutor.canonicalTutorId);
    return (
      <button
        disabled={busy}
        onClick={() => handleMarkPaid(tutor, paid)}
        style={{
          border: "none",
          borderRadius: "0.25rem",
          padding: "0.3rem 0.6rem",
          fontSize: "0.75rem",
          fontWeight: "600",
          cursor: busy ? "not-allowed" : "pointer",
          opacity: busy ? 0.6 : 1,
          backgroundColor: paid ? "#f1f5f9" : "#dcfce7",
          color: paid ? "#64748b" : "#166534",
          whiteSpace: "nowrap",
        }}
      >
        {busy ? "…" : paid ? "Unmark" : "Mark Paid"}
      </button>
    );
  };

  const emailBtn = (tutor) => {
    const entry = emailStatusMap[tutor.canonicalTutorId];
    const emailStatus = entry?.status;
    const busy = sendingEmails.has(tutor.canonicalTutorId);
    const isQueued = emailStatus === "PENDING" || emailStatus === "PROCESSING";
    const isSent = emailStatus === "SENT";
    const disabled = busy || isQueued || isSent;

    const cfg = {
      SENT:       { bg: "#dcfce7", color: "#166534", label: "Payslip: Sent" },
      PENDING:    { bg: "#fef9c3", color: "#854d0e", label: "Payslip: Queued" },
      PROCESSING: { bg: "#fef9c3", color: "#854d0e", label: "Payslip: Sending…" },
      FAILED:     { bg: "#fee2e2", color: "#991b1b", label: "Payslip: Retry" },
    }[emailStatus] || { bg: "#f1f5f9", color: "#001272", label: "Send Payslip" };

    return (
      <button
        disabled={disabled}
        onClick={() => handleSendEmail(tutor.canonicalTutorId)}
        title={entry?.lastError || undefined}
        style={{
          border: "none",
          borderRadius: "0.25rem",
          padding: "0.3rem 0.6rem",
          fontSize: "0.75rem",
          fontWeight: "600",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.7 : 1,
          backgroundColor: cfg.bg,
          color: cfg.color,
          whiteSpace: "nowrap",
        }}
      >
        {busy ? "…" : cfg.label}
      </button>
    );
  };

  const renderMobileCard = (t) => {
    const eligible = isWiseEligible(t);
    return (
    <div key={t.canonicalTutorId} style={styles.mobileCard}>
      <div style={styles.mobileHeader}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: "0.4rem" }}>
          {eligible && (
            <input
              type="checkbox"
              checked={selectedTutorIds.has(t.canonicalTutorId)}
              onChange={() => toggleSelectTutor(t.canonicalTutorId)}
              style={{ marginTop: "0.15rem", flexShrink: 0 }}
            />
          )}
          <div>
            <div style={{ fontWeight: "600", color: "#001272" }}>{t.tutorName}</div>
            {isLocked && (
              <div style={{ marginTop: "0.25rem", display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
                <StatusBadge status={t.paymentStatus} />
                <EmailStatusBadge status={emailStatusMap[t.canonicalTutorId]?.status} />
              </div>
            )}
          </div>
        </div>
        <div style={{ fontSize: "0.75rem", color: "#64748b" }}>{t.lineItemCount} items</div>
      </div>
      <div style={{ ...styles.mobileGrid, gridTemplateColumns: hasAdjustments ? "repeat(4, 1fr)" : "repeat(3, 1fr)" }}>
        <div>
          <div style={styles.mobileLabel}>Gross</div>
          <div style={{ ...styles.mobileValue, color: t.gross != null ? "#059669" : "#9ca3af" }}>{fmtAmt(t.gross)}</div>
        </div>
        <div>
          <div style={styles.mobileLabel}>Deductions</div>
          <div style={{ ...styles.mobileValue, color: t.deductions != null ? "#dc2626" : "#9ca3af" }}>{fmtAmt(t.deductions)}</div>
        </div>
        {hasAdjustments && (
          <div>
            <div style={styles.mobileLabel}>Adj.</div>
            <div style={{ ...styles.mobileValue, color: t.adjustments == null ? "#9ca3af" : parseFloat(t.adjustments) >= 0 ? "#7e22ce" : "#dc2626" }}>{fmtAmt(t.adjustments)}</div>
          </div>
        )}
        <div>
          <div style={styles.mobileLabel}>Net</div>
          <div style={{ ...styles.mobileValue, color: t.net != null ? "#0369a1" : "#9ca3af", fontWeight: "700" }}>{fmtAmt(t.net)}</div>
        </div>
      </div>
      {isLocked ? (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.4rem", marginTop: "0.75rem" }}>
          <button
            style={{ ...styles.viewBtn, gridColumn: "1 / -1" }}
            onClick={() => navigate(`/month/${yearMonth}/${t.canonicalTutorId}`)}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#000d5c"; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#001272"; }}
          >
            View Details
          </button>
          {pdfBtn(t)}
          {payBtn(t)}
          {emailBtn(t)}
        </div>
      ) : (
        <div style={{ marginTop: "0.75rem" }}>
          <button
            style={{ ...styles.viewBtn, width: "100%" }}
            onClick={() => navigate(`/month/${yearMonth}/${t.canonicalTutorId}`)}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#000d5c"; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#001272"; }}
          >
            View Details
          </button>
        </div>
      )}
    </div>
  );
  };

  return (
    <>
    <div style={styles.card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem", flexWrap: "wrap", gap: "0.5rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <h2 style={{ ...styles.cardTitle, margin: 0 }}>Payroll Details</h2>
          {isLocked && (
            <div style={{ display: "flex", borderRadius: "0.375rem", overflow: "hidden", border: "1px solid #e2e8f0", fontSize: "0.75rem", fontWeight: "600" }}>
              {["all", "unpaid"].map((f) => (
                <button
                  key={f}
                  onClick={() => setTutorFilter(f)}
                  style={{
                    border: "none",
                    padding: "0.25rem 0.65rem",
                    cursor: "pointer",
                    backgroundColor: tutorFilter === f ? "#001272" : "white",
                    color: tutorFilter === f ? "white" : "#64748b",
                    textTransform: "capitalize",
                  }}
                >
                  {f === "all" ? "All" : "Unpaid"}
                </button>
              ))}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
          {isLocked && selectedTutorIds.size > 0 && (
            <button
              onClick={() => setShowWiseModal(true)}
              style={{
                border: "none", borderRadius: "0.375rem",
                padding: "0.45rem 1rem", fontSize: "0.8rem",
                fontWeight: "600", cursor: "pointer",
                backgroundColor: "#1d4ed8", color: "white",
              }}
            >
              Initiate Wise Payment ({selectedTutorIds.size})
            </button>
          )}
          {isLocked && allPaid && (
            <span style={{ fontSize: "0.8rem", color: "#16a34a", fontWeight: "600" }}>
              ✓ All tutors paid
            </span>
          )}
        </div>
      </div>

      {!hasPayroll ? (
        <div style={{ backgroundColor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "0.25rem", padding: "1rem", textAlign: "center" }}>
          <p style={{ color: "#64748b", margin: 0 }}>
            Payroll has not been generated for this month yet.
          </p>
        </div>
      ) : isLoading ? (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "2rem" }}>
          <div style={{ border: "3px solid #f3f3f3", borderTop: "3px solid #001272", borderRadius: "50%", width: 36, height: 36, animation: "spin 1s linear infinite", marginBottom: "0.75rem" }} />
          <p style={{ color: "#001272", margin: 0 }}>Loading payroll data...</p>
        </div>
      ) : error ? (
        <div style={{ backgroundColor: "#fef3c7", border: "1px solid #fbbf24", borderRadius: "0.25rem", padding: "0.75rem" }}>
          <p style={{ color: "#92400e", margin: 0, fontSize: "0.875rem" }}>⚠️ Failed to load payroll data: {error.message}</p>
        </div>
      ) : tutors.length === 0 ? (
        <div style={{ backgroundColor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "0.25rem", padding: "1rem", textAlign: "center" }}>
          <p style={{ color: "#64748b", margin: 0 }}>No tutor payroll data for this month.</p>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div style={{ overflowX: "auto", display: isMobile ? "none" : "block" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" }}>
              <thead>
                <tr>
                  {isLocked && eligibleForWise.length > 0 && (
                    <th style={{ ...styles.th, width: "2.5rem", textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={allWiseSelected}
                        onChange={toggleSelectAll}
                        title="Select all eligible for Wise payment"
                      />
                    </th>
                  )}
                  <th style={{ ...styles.th, cursor: "pointer", userSelect: "none" }} onClick={() => setSortBy("name")}>
                    Tutor {sortBy === "name" ? "↑" : ""}
                  </th>
                  <th style={styles.thRight}>Gross</th>
                  <th style={styles.thRight}>Deductions</th>
                  {hasAdjustments && <th style={styles.thRight}>Adjustments</th>}
                  <th style={{ ...styles.thRight, cursor: "pointer", userSelect: "none" }} onClick={() => setSortBy("net")}>
                    Net {sortBy === "net" ? "↓" : ""}
                  </th>
                  <th style={styles.th}></th>
                </tr>
              </thead>
              <tbody>
                {sortedTutors.map((t) => (
                  <tr key={t.canonicalTutorId} style={t.paymentStatus === "PAID" ? { backgroundColor: "#f0fdf4" } : {}}>
                    {isLocked && eligibleForWise.length > 0 && (
                      <td style={{ ...styles.td, textAlign: "center", width: "2.5rem" }}>
                        {isWiseEligible(t) && (
                          <input
                            type="checkbox"
                            checked={selectedTutorIds.has(t.canonicalTutorId)}
                            onChange={() => toggleSelectTutor(t.canonicalTutorId)}
                          />
                        )}
                      </td>
                    )}
                    <td style={styles.td}>
                      <div style={{ fontWeight: "500", color: "#001272" }}>{t.tutorName}</div>
                      {isLocked && (
                        <div style={{ marginTop: "0.25rem", display: "flex", gap: "0.3rem", flexWrap: "wrap" }}>
                          <StatusBadge status={t.paymentStatus} />
                          <WisePayoutBadge status={t.wisePayoutStatus} onClick={t.wiseTransferId ? () => setWiseDetailTutor(t) : undefined} />
                          <EmailStatusBadge status={emailStatusMap[t.canonicalTutorId]?.status} />
                        </div>
                      )}
                    </td>
                    <td style={styles.tdRight}>
                      <span style={{ color: t.gross != null ? "#059669" : "#9ca3af" }}>{fmtAmt(t.gross)}</span>
                    </td>
                    <td style={styles.tdRight}>
                      <span style={{ color: t.deductions != null ? "#dc2626" : "#9ca3af" }}>{fmtAmt(t.deductions)}</span>
                    </td>
                    {hasAdjustments && (
                      <td style={styles.tdRight}>
                        <span style={{ color: t.adjustments == null ? "#9ca3af" : parseFloat(t.adjustments) >= 0 ? "#7e22ce" : "#dc2626" }}>
                          {fmtAmt(t.adjustments)}
                        </span>
                      </td>
                    )}
                    <td style={styles.tdRight}>
                      <span style={{ fontWeight: "600", color: t.net != null ? "#0369a1" : "#9ca3af" }}>{fmtAmt(t.net)}</span>
                    </td>
                    <td style={styles.td}>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                        <button
                          style={styles.viewBtn}
                          onClick={() => navigate(`/month/${yearMonth}/${t.canonicalTutorId}`)}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#000d5c"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#001272"; }}
                        >
                          View
                        </button>
                        {isLocked && pdfBtn(t)}
                        {isLocked && payBtn(t)}
                        {isLocked && emailBtn(t)}
                      </div>
                    </td>
                  </tr>
                ))}
                {/* Totals row */}
                <tr style={styles.summaryRow}>
                  {isLocked && eligibleForWise.length > 0 && <td style={styles.td}></td>}
                  <td style={styles.td}>
                    <strong>Total ({tutors.length} tutors)</strong>
                    {isLocked && (
                      <div style={{ fontSize: "0.75rem", color: "#64748b", marginTop: "0.2rem" }}>
                        {paidCount} paid · {tutors.length - paidCount} unpaid
                        {emailStatuses.length > 0 && (
                          <> · {sentEmailCount} email{sentEmailCount !== 1 ? "s" : ""} sent</>
                        )}
                      </div>
                    )}
                  </td>
                  {isSuperAdmin ? (
                    <>
                      <td style={styles.tdRight}><strong style={{ color: "#059669" }}>{formatCurrency(totalGross)}</strong></td>
                      <td style={styles.tdRight}><strong style={{ color: "#dc2626" }}>{formatCurrency(totalDeductions)}</strong></td>
                      {hasAdjustments && (
                        <td style={styles.tdRight}><strong style={{ color: totalAdjustments >= 0 ? "#7e22ce" : "#dc2626" }}>{formatCurrency(totalAdjustments)}</strong></td>
                      )}
                      <td style={styles.tdRight}><strong style={{ color: "#0369a1" }}>{formatCurrency(totalNet)}</strong></td>
                    </>
                  ) : (
                    <>
                      <td style={styles.tdRight}></td>
                      <td style={styles.tdRight}></td>
                      {hasAdjustments && <td style={styles.tdRight}></td>}
                      <td style={styles.tdRight}></td>
                    </>
                  )}
                  <td style={styles.td}></td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div style={{ display: isMobile ? "flex" : "none", flexDirection: "column", gap: "0.75rem", marginTop: "0.75rem" }}>
            {isLocked && eligibleForWise.length > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <input
                  type="checkbox"
                  checked={allWiseSelected}
                  onChange={toggleSelectAll}
                  id="wise-select-all-mobile"
                />
                <label htmlFor="wise-select-all-mobile" style={{ fontSize: "0.8rem", fontWeight: "600", color: "#1d4ed8", cursor: "pointer" }}>
                  {allWiseSelected ? "Deselect all" : `Select all Wise eligible (${eligibleForWise.length})`}
                </label>
              </div>
            )}
            {sortedTutors.map(renderMobileCard)}
            <div style={{ ...styles.mobileCard, backgroundColor: "#f0f9ff", borderColor: "#bae6fd" }}>
              <div style={{ fontWeight: "600", color: "#0369a1", marginBottom: "0.4rem" }}>
                Total ({tutors.length} tutors)
                {isLocked && (
                  <span style={{ fontSize: "0.75rem", color: "#64748b", fontWeight: "400", marginLeft: "0.5rem" }}>
                    {paidCount} paid · {tutors.length - paidCount} unpaid
                    {emailStatuses.length > 0 && ` · ${sentEmailCount} emailed`}
                  </span>
                )}
              </div>
              {isSuperAdmin && (
                <div style={{ ...styles.mobileGrid, gridTemplateColumns: hasAdjustments ? "repeat(4, 1fr)" : "repeat(3, 1fr)" }}>
                  <div>
                    <div style={styles.mobileLabel}>Gross</div>
                    <div style={{ ...styles.mobileValue, color: "#059669" }}>{formatCurrency(totalGross)}</div>
                  </div>
                  <div>
                    <div style={styles.mobileLabel}>Deductions</div>
                    <div style={{ ...styles.mobileValue, color: "#dc2626" }}>{formatCurrency(totalDeductions)}</div>
                  </div>
                  {hasAdjustments && (
                    <div>
                      <div style={styles.mobileLabel}>Adj.</div>
                      <div style={{ ...styles.mobileValue, color: totalAdjustments >= 0 ? "#7e22ce" : "#dc2626", fontWeight: "700" }}>{formatCurrency(totalAdjustments)}</div>
                    </div>
                  )}
                  <div>
                    <div style={styles.mobileLabel}>Net</div>
                    <div style={{ ...styles.mobileValue, color: "#0369a1", fontWeight: "700" }}>{formatCurrency(totalNet)}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

    </div>

    {markPaidTarget && (
      <MarkPaidModal
        tutor={markPaidTarget}
        yearMonth={yearMonth}
        onConfirm={handleConfirmPaid}
        onClose={() => setMarkPaidTarget(null)}
      />
    )}
    {wiseDetailTutor && (
      <WiseTransferModal
        tutor={wiseDetailTutor}
        yearMonth={yearMonth}
        onClose={() => setWiseDetailTutor(null)}
      />
    )}
    {showWiseModal && (
      <WisePaymentModal
        yearMonth={yearMonth}
        selectedTutorIds={selectedTutorIds}
        onClose={() => setShowWiseModal(false)}
        onSuccess={() => {
          setSelectedTutorIds(new Set());
          invalidateTutors();
        }}
      />
    )}
    </>
  );
};

export default PayrollTable;
